import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Drop-in replacement for `React.lazy` that recovers from a failed code-split
 * chunk import, whatever the cause.
 *
 * Belongs to: any shell (used by the shared router).
 *
 * **On the production incident that prompted this (vendor#585): the trigger
 * is deliberately NOT assumed.** The original hypothesis was "stale
 * `index.html` after a deploy references a chunk hash that no longer
 * exists" — every route is code-split into a content-hashed chunk, and if
 * the old hashed files are deleted from the host while a tab still holds the
 * old `index.html`, the next lazy navigation 404s. That is a real, general
 * failure mode this code still has to handle. But it is **not** what
 * happened in the incident that prompted this file's fix: the entry bundle
 * in the reporter's own stack trace was the *current* one, and the specific
 * chunk that failed to import serves a clean HTTP 200 straight from
 * production (fetched directly to confirm). So the file existed and the
 * document was current — the import still rejected client-side, for a
 * reason that remains undetermined between service-worker interference and
 * plain network flakiness. **The fix does not depend on knowing which.**
 * Every path below fires on any rejected `import()`, unconditionally, and
 * makes no assumption about why it rejected.
 *
 * On any such rejection we force a single full-page reload — but a naive
 * `window.location.reload()` is not sufficient by itself, for a reason that
 * *is* confirmed: `vite.config.ts` registers a service worker
 * (`VitePWA({ registerType: 'prompt', workbox: { navigateFallback:
 * '/index.html', ... } })`) that answers *every* navigation request —
 * reload included — from its own precache. If the currently-active SW's
 * precache is stale relative to what the server now has (the classic
 * post-deploy case above), an unassisted reload would serve the same stale
 * document again and the crash would repeat, regardless of `index.html`
 * being marked `no-cache` on the server (see `public/_headers`) — that
 * header only matters once a request reaches the network, and the SW
 * answers navigations without ever sending one.
 *
 * So before reloading we forcibly drop the SW's grip on this page:
 * unregister every service worker registration for the origin and delete
 * every Cache Storage entry (`bypassServiceWorkerCache`). Once unregistered,
 * the reload's navigation request has no worker left to intercept it and
 * goes straight to the network. This is a hardening that is safe and cheap
 * regardless of whether SW staleness was the actual cause this time — for
 * plain network flakiness it is a harmless no-op extra round trip before an
 * ordinary reload fixes things anyway. A cache-busting query string alone
 * would **not** achieve the same thing — Workbox's `navigateFallback` route
 * matches on `request.mode === 'navigate'`, not on the URL, so a live SW
 * would still intercept and still serve its precache regardless.
 *
 * Loop guard: the reload timestamp is stored in `sessionStorage`. If an import
 * still fails within {@link RELOAD_COOLDOWN_MS} of the last reload, the failure
 * is treated as one a reload cannot fix and is rethrown so the router's error
 * boundary can surface it instead of reloading forever. `shells/NotFound.tsx`
 * (that boundary) is written to give the user a working way out regardless of
 * why the import failed — see its docblock.
 */

const RELOAD_GUARD_KEY = 'mytcgbinder:last-chunk-reload';

/** Window after a reload during which a repeat failure is treated as unrecoverable-by-reload. */
const RELOAD_COOLDOWN_MS = 10_000;

function reloadedRecently(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    return Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage can throw in locked-down/private contexts — treat as "not
    // recently reloaded" so recovery still fires once.
    return false;
  }
}

function rememberReload(now: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Best-effort; if storage is unavailable the cooldown simply won't apply.
  }
}

/**
 * Unregisters every service worker registration for this origin and empties
 * every Cache Storage entry, so the reload that follows cannot be answered
 * from a stale precache. See the module docblock for why a plain reload is
 * not enough.
 *
 * Best-effort throughout: a browser without SW/Cache Storage support (or one
 * that throws mid-teardown) still falls through to the reload below — this
 * function never rejects.
 */
async function bypassServiceWorkerCache(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      // Best-effort — an origin with no controlling SW, or one that fails to
      // unregister, still gets the reload below.
    }
  }
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    } catch {
      // Best-effort, same rationale as above.
    }
  }
}

/**
 * Recovery handler for a failed code-split import. Exported for unit testing;
 * prefer {@link lazyWithReload} at call sites.
 *
 * Outside the cooldown it drops the service worker's control of this
 * page ({@link bypassServiceWorkerCache}), then triggers one full-page reload
 * and returns a promise that never settles (so the Suspense fallback stays up
 * until the navigation takes over). Inside the cooldown — meaning we *just*
 * reloaded and the import still failed — it rethrows so the error surfaces
 * instead of looping.
 */
export async function recoverFromChunkLoadError(error: unknown): Promise<never> {
  const now = Date.now();
  if (reloadedRecently(now)) throw error;
  rememberReload(now);
  await bypassServiceWorkerCache();
  window.location.reload();
  // Intentionally empty executor — the promise must never settle.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  return new Promise<never>(() => {});
}

// Props constraint is an empty-object component type: every route split point is
// rendered prop-less (`<SignInPage />`), and this satisfies `React.lazy`'s
// internal `ComponentType<any>` bound without reaching for `any` ourselves.
export function lazyWithReload<T extends ComponentType<Record<string, never>>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => factory().catch(recoverFromChunkLoadError));
}
